import { Send as SendIcon } from '@mui/icons-material';
import { Box, TextField, Button, Typography, CircularProgress } from '@mui/material';
import { EntityType } from '@nohara-dxdev/mt-common';
import { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CommentEntity } from '@/types/api';

import { useCreateComment } from '../api/create-comment';
import { useComments } from '../api/get-comments';

import { CommentItem } from './comment-item';

export interface ChatInterfaceProps {
  entityId: string;
  entityType: EntityType;
  path?: string;
  onClose?: () => void;
}

export const ChatInterface = ({ entityId, entityType, path = '' }: ChatInterfaceProps) => {
  const { t } = useTranslation();
  const [newComment, setNewComment] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const {
    data: comments,
    isLoading,
    error,
    nextPage,
    hasNextPage,
    isFetching,
  } = useComments({ entityId, path });

  const { mutateAsync: createComment, isPending: isCreating } = useCreateComment(
    entityId,
    entityType,
    {
      path,
    },
  );

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const reversedComments = useMemo(() => {
    return comments?.slice().reverse() ?? [];
  }, [comments]);

  useEffect(() => {
    scrollToBottom();
  }, [comments]);

  const handleSendComment = async () => {
    if (!newComment.trim()) return;

    try {
      await createComment(newComment.trim());
      setNewComment('');
    } catch (error) {
      console.error('Failed to create comment:', error);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void handleSendComment();
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box textAlign="center" py={4}>
        <Typography color="error">{t('comments.chat.error', 'Failed to load comments')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '500px', display: 'flex', flexDirection: 'column' }}>
      {/* Comments List */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {comments?.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography color="textSecondary">
              {t('comments.chat.noComments', 'No comments yet. Be the first to comment!')}
            </Typography>
          </Box>
        ) : (
          <Box>
            {reversedComments.map((comment: CommentEntity, index: number) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                showDivider={index < reversedComments.length - 1}
              />
            ))}
          </Box>
        )}

        {/* Load More Button */}
        {hasNextPage && (
          <Box textAlign="center" py={2}>
            <Button
              onClick={() => {
                nextPage();
              }}
              disabled={isFetching}
              variant="outlined"
              size="small"
            >
              {isFetching ? (
                <CircularProgress size={20} />
              ) : (
                t('comments.chat.loadMore', 'Load More')
              )}
            </Button>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* Comment Input */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            placeholder={t('comments.chat.placeholder', 'Type your comment...')}
            value={newComment}
            onChange={(e) => {
              setNewComment(e.target.value);
            }}
            onKeyDown={handleKeyPress}
            disabled={isCreating}
            variant="outlined"
            size="small"
          />
          <Button
            variant="contained"
            onClick={() => void handleSendComment()}
            disabled={!newComment.trim() || isCreating}
            sx={{ minWidth: 'auto', px: 2 }}
          >
            {isCreating ? <CircularProgress size={20} /> : <SendIcon />}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
